import React from 'react';
import { Col, Form } from 'react-bootstrap';
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
  getZipCode,
} from 'use-places-autocomplete';
import {
  showErrorToast,
  showSuccessToast,
} from '../../../common/helpers/Utils';

const LocationInput = ({
  labelText,
  inputName,
  value,
  placeholderText,
  formSection,
  onLocationChange,
  updateOtherFieldValue,
  errorMessage,
  disabled,
}) => {
  const {
    ready,
    value: inputValue,
    suggestions: { status, data: allSuggestions },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({ debounce: 300 });

  const data = allSuggestions; // Show all suggestions without filtering

  const showError = (label) => {
    let message = '';
    message = `please select ${label} first`;
    showErrorToast(message);
    clearSuggestions();
    setValue('', false);
    updateOtherFieldValue('', 'locality', formSection);
    updateOtherFieldValue('', 'city', formSection);
    updateOtherFieldValue('', 'state', formSection);
    updateOtherFieldValue('', 'country', formSection);
    updateOtherFieldValue('', 'zipcode', formSection);
  };

  const parseAddressParts = (description) => {
    const parts = description
      .split(',')
      .map((p) => p.trim())
      .filter((p) => p);

    let country = parts.pop() || '';
    let state = parts.pop() || '';
    let city = parts.pop() || '';
    let locality = parts.join(',') || '';
    return { locality, city, state, country };
  };

  const extractFeatureName = (components) => {
    const component = components.find((comp) => comp.types.includes('route'));
    if (component) return component.long_name;

    return '';
  };

  const extractAddressComponents = (components) => {
    let postalCode = '';
    let city = '';
    let state = '';
    let country = '';
    let locality = '';
    let subLocality = '';

    components.forEach((comp) => {
      const types = comp.types;
      const longName = comp.long_name;

      if (types.includes('postal_code')) {
        postalCode = longName;
      } else if (types.includes('locality')) {
        city = longName;
      } else if (types.includes('administrative_area_level_1')) {
        state = longName;
      } else if (types.includes('country')) {
        country = longName;
      } else if (
        types.includes('sublocality') ||
        types.includes('sublocality_level_1') ||
        types.includes('sublocality_level_2')
      ) {
        locality = longName;
      } else if (types.includes('route')) {
        subLocality = longName;
      }
    });

    return { postalCode, city, state, country, locality, subLocality };
  };

  const handleAddressSelect = async (suggestion) => {
    const { description, place_id } = suggestion;

    // Step 1: string split validation like mobile
    const parts = description
      .split(',')
      .map((p) => p.trim())
      .filter((p) => p);

    let locality = '';
    let city = '';
    let state = '';
    let country = '';

    if (parts.length >= 3) {
      country = parts.pop();
      state = parts.pop();
      city = parts.pop();
      locality = parts.join(', ');
    } else {
      showError(inputName);
      return;
    }

    try {
      // Step 2: Fetch structured data
      const results = await getGeocode({ placeId: place_id });
      const { lat, lng } = getLatLng(results[0]);
      const components = results[0]?.address_components || [];

      const parsed = extractAddressComponents(components);

      // Step 3: Merge locality + subLocality
      let finalLocality = '';
      if (parsed.subLocality) {
        finalLocality = parsed.locality
          ? `${parsed.subLocality} ${parsed.locality}`
          : parsed.subLocality;
      } else if (parsed.locality) {
        finalLocality = parsed.locality;
      } else {
        finalLocality = locality; // fallback from string split
      }

      const locationDetails = {
        locality: finalLocality,
        city: parsed.city || city,
        state: parsed.state || state,
        country: parsed.country || country,
        zipcode: parsed.postalCode || '',
        latitude: lat,
        longitude: lng,
      };

      // Step 4: update field values with no leading commas
      if (inputName === 'locality') {
        setValue(
          [
            locationDetails.locality,
            locationDetails.city,
            locationDetails.state,
            locationDetails.zipcode,
            locationDetails.country,
          ]
            .filter((part) => part)
            .join(', ')
            .trim(),
          false
        );
        onLocationChange(locationDetails, formSection);
      } else {
        setValue(description, false);
        updateOtherFieldValue(
          locationDetails[inputName],
          inputName,
          formSection
        );
      }

      clearSuggestions();
    } catch (error) {
      console.error('Error fetching geocode info:', error);
      showSuccessToast('Failed to fetch address details.');
    }
  };

  const renderLocationSuggestions = () => {
    const selectedItems = data.map((suggestion) => {
      const { place_id, structured_formatting } = suggestion;
      const mainText = structured_formatting?.main_text || '';
      const secondaryText = structured_formatting?.secondary_text || '';

      const displayText = `${mainText}${
        secondaryText ? `, ${secondaryText}` : ''
      }`;

      return (
        <li
          key={place_id}
          className="location-suggestion-item"
          onClick={() => handleAddressSelect(suggestion)}
        >
          {displayText}
        </li>
      );
    });

    return selectedItems.length > 0 ? selectedItems : <h6>No data found</h6>;
  };

  console.log('value', value);
  return (
    <Form.Group className="custom-form">
      <Form.Label className="color-head form-label">{labelText}</Form.Label>
      <Form.Control
        type="text"
        className={
          inputName === 'city' ||
          inputName === 'country' ||
          inputName === 'state'
            ? 'form-control-disabled'
            : ''
        }
        value={inputValue || value}
        onChange={(e) => {
          const newValue = e.target.value;
          setValue(newValue);
          updateOtherFieldValue(newValue, inputName, formSection);
        }}
        disabled={
          !ready ||
          disabled ||
          inputName === 'city' ||
          inputName === 'country' ||
          inputName === 'state'
        }
        placeholder={placeholderText}
      />
      {status === 'OK' && (
        <ul className="location-suggestions" style={{top: '85px'}}>{renderLocationSuggestions()}</ul>
      )}
      {errorMessage && (
        <Form.Text className="text-danger">{errorMessage}</Form.Text>
      )}
    </Form.Group>
  );
};

export default LocationInput;
